"use client"

import { useState, useEffect } from "react"
import { useParams, useNavigate } from "react-router-dom"
import {
  ChevronDown,
  ChevronUp,
  CheckSquare,
  FileText,
  AlignLeft,
  List,
  MessageSquare,
  User,
  Award,
  Calendar,
  AlertCircle,
  BookOpen,
  Star,
  CheckCircle,
  XCircle,
  ImageIcon,
  ArrowLeft,
  Loader,
  ChevronRight,
} from "lucide-react"
import { getQuizSubmissionDetail } from "./api"
import { postAws } from "../postAws"
import { generatePresignedUrl } from "../postFileAws"
import { BlockMath, InlineMath } from "react-katex"
import ReactMarkdown from "react-markdown"
import "katex/dist/katex.min.css"

const getQuestionTypeIcon = (questionType) => {
  switch (questionType) {
    case "Multiple-Choice":
    case "MCQ":
      return <CheckSquare size={20} className="text-primary" />
    case "Short Answer":
    case "Short-Answer":
      return <FileText size={20} className="text-primary" />
    case "Essay":
      return <AlignLeft size={20} className="text-primary" />
    default:
      return <List size={20} className="text-primary" />
  }
}

const renderMathText = (text) => {
  if (!text) return null
  const parts = text.split(/(\$\$[\s\S]*?\$\$|\$[\s\S]*?\$)/)
  return parts.map((part, i) => {
    if (part.startsWith("$$") && part.endsWith("$$")) {
      return <BlockMath key={i}>{part.slice(2, -2)}</BlockMath>
    } else if (part.startsWith("$") && part.endsWith("$")) {
      return <InlineMath key={i}>{part.slice(1, -1)}</InlineMath>
    } else {
      return <span key={i}>{part}</span>
    }
  })
}

const ImageDropdown = ({ imageUrl }) => {
  const [isOpen, setIsOpen] = useState(false)

  return (
    <div className="mt-4 group">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center justify-between w-full px-5 py-3.5 text-sm font-medium text-left text-white 
                   bg-gradient-to-r from-blue-500 to-blue-600 rounded-xl 
                   hover:from-blue-600 hover:to-blue-700 transition-all duration-300 shadow-md 
                   hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-blue-400 focus:ring-offset-2"
      >
        <span className="flex items-center">
          <ImageIcon className="w-5 h-5 mr-2" />
          View Question Image
        </span>
        <div className="flex items-center justify-center w-6 h-6 rounded-full bg-blue-700 transition-transform duration-300">
          {isOpen ? <ChevronUp className="w-4 h-4 text-white" /> : <ChevronDown className="w-4 h-4 text-white" />}
        </div>
      </button>

      {isOpen && (
        <div className="mt-4 transition-all duration-500 ease-in-out transform">
          <div className="relative overflow-hidden rounded-xl shadow-xl bg-white p-1.5 border border-gray-100">
            <img
              src={imageUrl || "/placeholder.svg"}
              alt="Question"
              className="w-full rounded-lg object-contain hover:scale-[1.02] transition-all duration-300"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
          </div>
        </div>
      )}
    </div>
  )
}

const renderMarkdown = (text) => {
  return (
    <ReactMarkdown
      components={{
        h1: ({ node, ...props }) => <h1 className="text-2xl font-bold mt-6 mb-3 text-gray-800" {...props} />,
        h2: ({ node, ...props }) => <h2 className="text-xl font-bold mt-5 mb-2 text-gray-800" {...props} />,
        h3: ({ node, ...props }) => <h3 className="text-lg font-semibold mt-4 mb-2 text-gray-800" {...props} />,
        h4: ({ node, ...props }) => <h4 className="text-base font-semibold mt-3 mb-1 text-gray-800" {...props} />,
        p: ({ node, ...props }) => <p className="mb-3 text-gray-700 leading-relaxed" {...props} />,
        ul: ({ node, ...props }) => <ul className="list-disc pl-5 mb-3 text-gray-700 space-y-1" {...props} />,
        ol: ({ node, ...props }) => <ol className="list-decimal pl-5 mb-3 text-gray-700 space-y-1" {...props} />,
        li: ({ node, ...props }) => <li className="mb-1" {...props} />,
        strong: ({ node, ...props }) => <strong className="font-bold text-gray-900" {...props} />,
        a: ({ node, ...props }) => <a className="text-primary hover:underline font-medium" {...props} />,
        blockquote: ({ node, ...props }) => (
          <blockquote className="pl-4 border-l-4 border-primary/30 italic text-gray-600 my-4" {...props} />
        ),
        code: ({ node, ...props }) => (
          <code className="bg-gray-100 px-1.5 py-0.5 rounded text-sm font-mono text-primary-dark" {...props} />
        ),
      }}
    >
      {text}
    </ReactMarkdown>
  )
}

export default function TeacherQuizResponseView() {
  const { id } = useParams()
  const navigate = useNavigate()
  const [quizData, setQuizData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [showQuestionImage, setShowQuestionImage] = useState({})
  const [questionImages, setQuestionImages] = useState({})

  const handleBackToQuizzes = () => {
    navigate("/quiz")
  }

  useEffect(() => {
    const fetchQuizDetails = async () => {
      try {
        setLoading(true)
        const response = await getQuizSubmissionDetail(id)
        const data = JSON.parse(response.body).data

        if (data.status !== "evaluated") {
          const evalResponse = await postAws("https://api-dev.aurait.ai/quizEval", "POST", { id: data._id })
          const evalData = JSON.parse(evalResponse.body)

          // Fetch the updated quiz submission details after evaluation
          const updatedResponse = await getQuizSubmissionDetail(id)
          const updatedData = JSON.parse(updatedResponse.body).data
          setQuizData(updatedData)
        } else {
          setQuizData(data)
        }

        // Load question images
        const updatedQuestions = await Promise.all(
          data.quizQuestions.map(async (question, index) => {
            if (question.questionImageUrl) {
              try {
                const presignedUrl = await generatePresignedUrl(question.questionImageUrl)
                setQuestionImages((prev) => ({ ...prev, [index]: presignedUrl }))
              } catch (error) {
                console.error("Error generating presigned URL for question image:", error)
              }
            }
            return question
          }),
        )

        setError(null)
      } catch (err) {
        console.error("Error fetching quiz details:", err)
        setError("Failed to load quiz details. Please try again.")
      } finally {
        setLoading(false)
      }
    }

    if (id) {
      fetchQuizDetails()
    }
  }, [id])

  const toggleQuestionImage = async (questionIndex) => {
    setShowQuestionImage((prev) => ({ ...prev, [questionIndex]: !prev[questionIndex] }))
    if (!showQuestionImage[questionIndex] && !questionImages[questionIndex]) {
      try {
        const question = quizData?.quizQuestions[questionIndex]
        if (question && question.questionImageUrl) {
          const url = await generatePresignedUrl(question.questionImageUrl)
          setQuestionImages((prev) => ({ ...prev, [questionIndex]: url }))
        }
      } catch (error) {
        console.error("Error generating presigned URL for question image:", error)
      }
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-50 to-blue-100 flex flex-col justify-center items-center -mt-6">
        <div className="flex flex-col items-center justify-center py-16">
          <Loader className="w-10 h-10 text-indigo-600 animate-spin mb-4" />
          <p className="text-gray-600 font-medium">Loading student's response...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-indigo-50 to-blue-100 -mt-6">
        <div className="bg-white p-8 rounded-xl shadow-xl max-w-md w-full text-center">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-800 mb-2">Error Loading Data</h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            onClick={handleBackToQuizzes}
            className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors duration-300 shadow-md"
          >
            Try Again
          </button>
        </div>
      </div>
    )
  }

  if (!quizData) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-indigo-50 to-blue-100 -mt-6">
        <div className="bg-white p-8 rounded-xl shadow-xl max-w-md w-full text-center">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-800 mb-2">No Data Found</h2>
          <p className="text-gray-600">No quiz data is available for this submission.</p>
        </div>
      </div>
    )
  }

  const totalMarks = quizData.quizQuestions.reduce((total, q) => total + (q.obtainedMarks || 0), 0)
  const maxMarks = quizData.quizQuestions.reduce((total, q) => total + (q.maxMarks || 0), 0)
  const scorePercentage = Math.round((totalMarks / maxMarks) * 100) || 0

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 to-blue-100 -mt-6">
      <div className="container mx-auto px-2 md:px-4 py-0 max-w-7xl">
        {/* Header Section with Quiz Info */}
        <div className="bg-white rounded-xl md:rounded-2xl shadow-md overflow-hidden mt-6 md:mt-8">
          <div className="bg-gradient-to-r from-indigo-500 to-blue-600 px-3 md:px-4 py-3 md:py-4">
            {/* Mobile Layout */}
            <div className="block lg:hidden">
              {/* Row 1: Breadcrumb and Title */}
              <div className="flex items-center gap-2 mb-3">
                <div className="flex items-center text-white/80 text-xs">
                  <button onClick={handleBackToQuizzes} className="hover:text-white transition-colors">
                    Quizzes
                  </button>
                  <ChevronRight className="w-3 h-3 mx-1" />
                  <span className="text-white font-medium">Chemistry Quiz</span>
                </div>
              </div>

              {/* Row 2: Quiz Title */}
              <div className="flex-1 min-w-0 mb-3">
                <h1 className="text-sm font-bold text-white truncate">{quizData.quizTitle || "Chemistry Quiz"}</h1>
              </div>

              {/* Row 3: Student Info and Score */}
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center bg-white/20 backdrop-blur-sm text-white rounded-md px-2 py-1 flex-shrink-0">
                  <User className="w-3 h-3 mr-1" />
                  <span className="text-xs font-medium">{quizData.studentName}</span>
                </div>
                <div className="flex items-center bg-white/20 backdrop-blur-sm text-white rounded-md px-2 py-1 flex-shrink-0">
                  <Calendar className="w-3 h-3 mr-1" />
                  <span className="text-xs font-medium">
                    {new Date(quizData.createdAt).toLocaleDateString("en-US", {
                      month: "short",
                      day: "numeric",
                    })}
                  </span>
                </div>
                <div className="flex items-center bg-white/20 backdrop-blur-sm text-white rounded-md px-2 py-1 flex-shrink-0">
                  <Award className="w-3 h-3 mr-1" />
                  <span className="text-xs font-medium">
                    {totalMarks}/{maxMarks}
                  </span>
                </div>
              </div>
            </div>

            {/* Desktop Layout */}
            <div className="hidden lg:flex lg:items-center justify-between gap-4">
              {/* Left: Breadcrumb and Title */}
              <div className="flex flex-col gap-1">
                <div className="flex items-center text-white/80 text-sm">
                  <button
                    onClick={handleBackToQuizzes}
                    className="hover:text-white transition-colors flex items-center"
                  >
                    <ArrowLeft className="w-4 h-4 mr-1" />
                    Quizzes
                  </button>
                  <ChevronRight className="w-4 h-4 mx-1" />
                  <span className="text-white font-medium">Chemistry Quiz</span>
                </div>
                <h1 className="text-xl lg:text-2xl font-bold text-white">{quizData.quizTitle || "Chemistry Quiz"}</h1>
              </div>

              {/* Right: Student Info and Score */}
              <div className="flex items-center gap-4">
                <div className="flex items-center bg-white/20 backdrop-blur-sm text-white rounded-md px-3 py-1.5">
                  <User className="w-4 h-4 mr-2" />
                  <div>
                    <p className="text-xs font-medium mb-0.5">Student</p>
                    <p className="text-sm font-bold">{quizData.studentName}</p>
                  </div>
                </div>
                <div className="flex items-center bg-white/20 backdrop-blur-sm text-white rounded-md px-3 py-1.5">
                  <Calendar className="w-4 h-4 mr-2" />
                  <div>
                    <p className="text-xs font-medium mb-0.5">Submitted</p>
                    <p className="text-sm font-bold">
                      {new Date(quizData.createdAt).toLocaleDateString("en-US", {
                        year: "numeric",
                        month: "short",
                        day: "numeric",
                      })}
                    </p>
                  </div>
                </div>
                <div className="flex items-center bg-white/20 backdrop-blur-sm text-white rounded-md px-3 py-1.5">
                  <Award className="w-4 h-4 mr-2" />
                  <div>
                    <p className="text-xs font-medium mb-0.5">Score</p>
                    <p className="text-sm font-bold">
                      {totalMarks}/{maxMarks} ({scorePercentage}%)
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="p-3 md:p-6">
            {/* Questions */}
            <div className="space-y-6">
              {quizData.quizQuestions.map((question, index) => {
                const isCorrect = quizData.status === "evaluated" && Number(question.obtainedMarks) > 0
                const isIncorrect = quizData.status === "evaluated" && Number(question.obtainedMarks) === 0

                return (
                  <div key={index} className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden">
                    <div className="bg-gray-50 px-4 py-3 border-b border-gray-200 flex justify-between items-center">
                      <div className="flex items-center">
                        <div className="p-1.5 rounded-md bg-primary/10 mr-3">
                          {getQuestionTypeIcon(question.questionType || "Multiple-Choice")}
                        </div>
                        <h3 className="text-base font-semibold text-gray-800">Question {index + 1}</h3>
                      </div>

                      {quizData.status === "evaluated" && (
                        <div
                          className={`flex items-center gap-1.5 px-3 py-1 rounded-full text-sm ${
                            isCorrect ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"
                          }`}
                        >
                          {isCorrect ? <CheckCircle className="w-4 h-4" /> : <XCircle className="w-4 h-4" />}
                          <span className="font-medium">
                            {question.obtainedMarks} / {question.maxMarks}
                          </span>
                        </div>
                      )}
                    </div>

                    <div className="p-4">
                      <div className="text-gray-700 mb-4 prose max-w-none leading-relaxed">
                        {renderMathText(question.question)}
                      </div>

                      {question.questionImageUrl && <ImageDropdown imageUrl={questionImages[index]} />}

                      {question.options && question.options.length > 0 && (
                        <div className="mb-4 bg-gray-50 p-4 rounded-lg border border-gray-100">
                          <h4 className="text-base font-semibold mb-3 text-gray-800 flex items-center">
                            <CheckSquare className="w-4 h-4 mr-2 text-primary" />
                            Multiple Choice Options
                          </h4>
                          <ol className="space-y-2">
                            {question.options.map((option, optionIndex) => {
                              const isStudentAnswer = option === question.answer
                              const isCorrectAnswer = option === question.correctAnswer

                              return (
                                <li
                                  key={optionIndex}
                                  className={`flex items-start p-3 rounded-lg ${
                                    isCorrectAnswer
                                      ? "bg-green-50 border border-green-200"
                                      : isStudentAnswer && !isCorrectAnswer
                                        ? "bg-red-50 border border-red-200"
                                        : "bg-white border border-gray-200"
                                  }`}
                                >
                                  <div className="flex items-center justify-center min-w-[24px] h-6 rounded-full bg-primary/10 text-primary text-sm font-medium mr-3">
                                    {String.fromCharCode(65 + optionIndex)}
                                  </div>
                                  <div className="flex-1 text-gray-700">{renderMathText(option)}</div>
                                  <div className="flex items-center ml-2">
                                    {isStudentAnswer && (
                                      <span className="text-xs font-medium mr-2 text-blue-600 bg-blue-100 px-2 py-0.5 rounded-full">
                                        Student's Answer
                                      </span>
                                    )}
                                    {isCorrectAnswer ? (
                                      <CheckCircle className="w-5 h-5 text-green-600" />
                                    ) : isStudentAnswer ? (
                                      <XCircle className="w-5 h-5 text-red-600" />
                                    ) : null}
                                  </div>
                                </li>
                              )
                            })}
                          </ol>
                        </div>
                      )}

                      <div className="mb-4">
                        <h4 className="text-base font-semibold mb-3 text-gray-800 flex items-center">
                          <User className="w-4 h-4 mr-2 text-primary" />
                          Student's Answer
                        </h4>
                        {question.answer ? (
                          <div className="text-gray-700 bg-gray-50 p-4 rounded-lg border-l-4 border-primary">
                            {renderMathText(question.answer)}
                          </div>
                        ) : (
                          <div className="text-yellow-600 bg-yellow-50 p-4 rounded-lg border border-yellow-200 flex items-center">
                            <AlertCircle className="w-5 h-5 mr-3 text-yellow-500" />
                            No answer was submitted for this question.
                          </div>
                        )}
                      </div>

                      {quizData.status === "evaluated" && (
                        <div className="space-y-4">
                          <div
                            className={`p-4 rounded-lg ${
                              isCorrect ? "bg-green-50 border border-green-100" : "bg-red-50 border border-red-100"
                            }`}
                          >
                            <div className="flex justify-between items-center mb-2">
                              <h4
                                className={`text-base font-semibold flex items-center ${
                                  isCorrect ? "text-green-800" : "text-red-800"
                                }`}
                              >
                                <MessageSquare className="w-4 h-4 mr-2" />
                                Auto-Evaluation Result
                              </h4>
                              <div className="flex items-center">
                                {isCorrect ? (
                                  <Star className="w-5 h-5 text-yellow-400 fill-yellow-400" />
                                ) : (
                                  <AlertCircle className="w-5 h-5 text-red-500" />
                                )}
                              </div>
                            </div>
                            <p
                              className={`${isCorrect ? "text-green-700" : "text-red-700"} whitespace-pre-line leading-relaxed`}
                            >
                              {isCorrect
                                ? "Correct answer! The student selected the right option."
                                : "Incorrect answer. The student selected the wrong option."}
                            </p>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )
              })}
            </div>

            {/* Overall Summary */}
            {quizData.status === "evaluated" && (
              <div className="mt-8 bg-gray-50 rounded-lg p-5 border border-gray-200">
                <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
                  <BookOpen className="w-5 h-5 text-primary mr-2" />
                  Quiz Summary
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm">
                    <h3 className="text-sm font-semibold text-gray-600 mb-1">Total Questions</h3>
                    <p className="text-2xl font-bold text-gray-900">{quizData.quizQuestions.length}</p>
                  </div>
                  <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm">
                    <h3 className="text-sm font-semibold text-gray-600 mb-1">Correct Answers</h3>
                    <p className="text-2xl font-bold text-green-600">
                      {quizData.quizQuestions.filter((q) => Number(q.obtainedMarks) > 0).length}
                    </p>
                  </div>
                  <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm">
                    <h3 className="text-sm font-semibold text-gray-600 mb-1">Final Score</h3>
                    <p className="text-2xl font-bold text-primary">
                      {totalMarks}/{maxMarks} ({scorePercentage}%)
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
